<template>
    <div>
        <!-- Hero Section -->
        <v-container
            fluid
            class="hero-section d-flex pa-0"
        >
            <v-col
                cols="12"
                class="d-flex align-center justify-center text-center"
            >
                <div class="hero-content pa-8">
                    <h1 class="display-1 font-weight-bold text-primary mb-4">
                        Thinking Outside the Timebox
                    </h1>
                    <p class="text-h6 text-grey-darken-1 mb-6">
                        Most scheduling systems create winners and losers from
                        day one. But what if everyone could start with a fair
                        shot at their ideal time?
                    </p>
                </div>
            </v-col>
        </v-container>

        <!-- Transformation Story Section -->
        <v-container
            fluid
            class="grey-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <h2 class="text-h4 text-center mb-8 text-primary">
                            The Power of Starting Fresh
                        </h2>
                        <p class="text-h6 text-center text-grey-darken-1 mb-8">
                            Real transformations happen when everyone gets a
                            fair chance at their ideal time slot
                        </p>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        v-for="(transformation, index) in transformations"
                        :key="index"
                        cols="12"
                        md="4"
                        class="mb-6"
                    >
                        <div class="text-center">
                            <v-avatar
                                :color="transformation.color"
                                size="80"
                                class="mb-4"
                            >
                                <v-icon
                                    :icon="transformation.icon"
                                    size="40"
                                    color="white"
                                />
                            </v-avatar>
                            <h3 class="text-h6 mb-3">
                                {{ transformation.title }}
                            </h3>
                            <p class="text-body-1 text-grey-darken-1">
                                {{ transformation.description }}
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- The Philosophy Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-8 text-primary">
                        A Different Philosophy
                    </h2>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                    md="8"
                    class="mx-auto"
                >
                    <v-card
                        class="pa-6"
                        elevation="2"
                    >
                        <p class="text-body-1 mb-4 text-grey-darken-2">
                            <strong
                                >Traditional scheduling creates winners and
                                losers.</strong
                            >
                            Students who book early get the prime time slots,
                            while new students are squeezed into whatever's
                            left. This builds up scheduling baggage over time.
                        </p>
                        <p class="text-body-1 mb-4 text-grey-darken-2">
                            <strong>Studoku takes a different approach.</strong>
                            We regularly ask everyone to rate all available time
                            slots, then optimize the entire schedule from
                            scratch to give as many students as possible their
                            preferred times.
                        </p>
                        <p class="text-body-1 text-grey-darken-2">
                            <strong>The result?</strong> Students who seemed
                            difficult or unmotivated often just needed their
                            optimal time slot. When timing aligns with student's
                            preferred schedules, attitudes change and learning
                            accelerates.
                        </p>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- The Studoku Solution Section -->
        <v-container
            fluid
            class="blue-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <h2 class="text-h4 text-center mb-8 text-primary">
                            How Studoku Works
                        </h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        v-for="(step, index) in studokuSteps"
                        :key="index"
                        cols="12"
                        md="6"
                        lg="3"
                        class="mb-6"
                    >
                        <v-card
                            class="h-100 text-center pa-6"
                            elevation="2"
                            hover
                        >
                            <v-avatar
                                :color="step.color"
                                size="64"
                                class="mb-4"
                            >
                                <v-icon
                                    :icon="step.icon"
                                    size="32"
                                    color="white"
                                />
                            </v-avatar>
                            <h3 class="text-h6 mb-3">
                                {{ step.title }}
                            </h3>
                            <p class="text-body-2 text-grey-darken-1">
                                {{ step.description }}
                            </p>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- Scheduling Philosophy Comparison Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-8 text-primary">
                        Two Approaches to Scheduling
                    </h2>
                    <p class="text-h6 text-center text-grey-darken-1 mb-8">
                        Different philosophies lead to different outcomes for
                        you and your students
                    </p>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                    md="6"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 pa-6"
                        elevation="2"
                        color="blue-grey-lighten-5"
                    >
                        <div class="d-flex align-center mb-4">
                            <v-icon
                                :icon="mdiCalendarClock"
                                size="32"
                                color="blue-grey"
                                class="mr-3"
                            />
                            <h3 class="text-h6 text-blue-grey-darken-2">
                                First-Come, First-Served Philosophy
                            </h3>
                        </div>
                        <v-list class="bg-transparent">
                            <v-list-item
                                v-for="characteristic in traditionalCharacteristics"
                                :key="characteristic"
                                class="px-0 py-1"
                            >
                                <template #prepend>
                                    <v-icon
                                        :icon="mdiCircleMedium"
                                        color="blue-grey"
                                        size="20"
                                        class="mr-2"
                                    />
                                </template>
                                <v-list-item-title
                                    class="text-body-2 text-wrap"
                                >
                                    {{ characteristic }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-col>
                <v-col
                    cols="12"
                    md="6"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 pa-6"
                        elevation="2"
                        color="green-lighten-5"
                    >
                        <div class="d-flex align-center mb-4">
                            <v-icon
                                :icon="mdiCalendarHeart"
                                size="32"
                                color="green"
                                class="mr-3"
                            />
                            <h3 class="text-h6 text-green-darken-2">
                                Optimization Philosophy
                            </h3>
                        </div>
                        <v-list class="bg-transparent">
                            <v-list-item
                                v-for="characteristic in studokuCharacteristics"
                                :key="characteristic"
                                class="px-0 py-1"
                            >
                                <template #prepend>
                                    <v-icon
                                        :icon="mdiCircleMedium"
                                        color="green"
                                        size="20"
                                        class="mr-2"
                                    />
                                </template>
                                <v-list-item-title
                                    class="text-body-2 text-wrap"
                                >
                                    {{ characteristic }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Results Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-8 text-primary">
                        The Results Speak for Themselves
                    </h2>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                    md="4"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 text-center pa-6"
                        elevation="2"
                        color="purple-lighten-5"
                    >
                        <v-icon
                            :icon="mdiHeart"
                            size="48"
                            color="purple"
                            class="mb-4"
                        />
                        <h3 class="text-h6 mb-3 text-purple-darken-2">
                            Stronger Relationships
                        </h3>
                        <p class="text-body-1 text-grey-darken-1">
                            Scheduling becomes collaborative rather than
                            competitive.
                        </p>
                    </v-card>
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 text-center pa-6"
                        elevation="2"
                        color="orange-lighten-5"
                    >
                        <v-icon
                            :icon="mdiEmoticonHappy"
                            size="48"
                            color="orange"
                            class="mb-4"
                        />
                        <h3 class="text-h6 mb-3 text-orange-darken-2">
                            Joyful Lessons
                        </h3>
                        <p class="text-body-1 text-grey-darken-1">
                            Each fresh start means everyone gets a chance at an
                            upgrade.
                        </p>
                    </v-card>
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 text-center pa-6"
                        elevation="2"
                        color="teal-lighten-5"
                    >
                        <v-icon
                            :icon="mdiTrendingUp"
                            size="48"
                            color="teal"
                            class="mb-4"
                        />
                        <h3 class="text-h6 mb-3 text-teal-darken-2">
                            Faster Progress
                        </h3>
                        <p class="text-body-1 text-grey-darken-1">
                            Better times mean better attitudes, which lead to
                            better progress.
                        </p>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- Call to Action Section -->
        <v-container
            fluid
            class="bg-primary py-12"
        >
            <v-container>
                <v-row>
                    <v-col
                        cols="12"
                        class="text-center"
                    >
                        <h2 class="text-h4 text-white font-weight-bold mb-3">
                            Ready to try Studoku?
                        </h2>
                        <!-- Conditional content based on feature flag -->
                        <div v-if="store.hasFeature('self-signup')">
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                Create your account and start building better
                                schedules today.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                aria-label="Sign up for Studoku"
                                to="/signup"
                            >
                                Get Started Free
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Start creating better schedules in minutes.
                            </p>
                        </div>
                        <div v-else>
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                We're launching soon. Join our waitlist and
                                we'll let you know when it's ready.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                aria-label="Join the Studoku waitlist"
                                @click="waitlistModal = true"
                            >
                                Join Waitlist
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Just your email. No spam.
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- Waitlist Dialog Component - only when self-signup is disabled -->
        <WaitlistModal
            v-if="!store.hasFeature('self-signup')"
            v-model="waitlistModal"
        />
    </div>
</template>

<script lang="ts" setup>
import {
    mdiArrowRight,
    mdiCalendarClock,
    mdiCalendarHeart,
    mdiCalendarRefresh,
    mdiCircleMedium,
    mdiEmoticonHappy,
    mdiEmoticonHappyOutline,
    mdiHeart,
    mdiRefresh,
    mdiStar,
    mdiTarget,
    mdiTrendingUp,
} from '@mdi/js';
import { ref } from 'vue';

import WaitlistModal from '@/shared/components/modals/WaitlistModal.vue';
import { useAppStore } from '@/shared/store';

const store = useAppStore();
const waitlistModal = ref(false);

const traditionalCharacteristics = [
    'Teachers find gaps and offer them one by one',
    'Students accommodate whatever times are available',
    'Schedule becomes rigid and difficult to change',
    'New students get stuck with leftover time slots',
    'Teachers often compromise personal time to accommodate students',
];

const studokuCharacteristics = [
    'Everyone rates all available times before scheduling',
    'Algorithm optimizes for maximum student satisfaction',
    'Regular fresh starts prevent schedule baggage',
    'Every student is given a fair chance at their ideal time',
    "Teachers' availability comes first while still accommodating students",
];

const transformations = [
    {
        title: 'From Struggle to Joy',
        description:
            'Students who seemed difficult or unmotivated often just needed their perfect time slot to transform into engaged learners.',
        icon: mdiEmoticonHappyOutline,
        color: 'blue',
    },
    {
        title: 'No More Baggage',
        description:
            'Starting fresh each period means no student is stuck with a bad time just because they joined late or circumstances changed.',
        icon: mdiRefresh,
        color: 'green',
    },
    {
        title: 'Optimal for Everyone',
        description:
            'Our optimization ensures the maximum number of students get their preferred times, not just the ones who booked first.',
        icon: mdiTarget,
        color: 'orange',
    },
];

const studokuSteps = [
    {
        title: 'Rate All Times',
        description:
            'Everyone rates each available time slot: Impossible, Doable but Difficult, Neutral, Works Well, or Perfect.',
        color: 'blue',
        icon: mdiStar,
    },
    {
        title: 'Fresh Optimization',
        description:
            'Our algorithm starts with a clean slate and optimizes the entire schedule to give the most students their best times.',
        color: 'green',
        icon: mdiRefresh,
    },
    {
        title: 'Maximum Satisfaction',
        description:
            'The result maximizes overall happiness rather than just accommodating whoever booked first.',
        color: 'orange',
        icon: mdiTarget,
    },
    {
        title: 'Regular Reset',
        description:
            'Start fresh each scheduling period as circumstances and preferences change over time.',
        color: 'purple',
        icon: mdiCalendarRefresh,
    },
];
</script>

<style scoped>
.hero-section {
    background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
    min-height: 400px;
}

.hero-content {
    max-width: 800px;
}
</style>
